import { i18n } from "../i18n/i18n";

import React, { Component } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { produce } from 'immer'
import { findIndex, map, pullAt, reject } from 'lodash'
import routes from '../routes/routes'
import confirmService from '../services/confirm.service'
import itemService from '../services/item.service'
import customerService from '../services/customer.service'
import contactSiteService from '../services/contactSite.service'
import itemApi, { ItemData } from '../apis/item.api'
import customerApi, { CustomerData } from '../apis/customer.api'
import { ContactSiteData } from '../apis/contactSite.api'
import { ItemType } from '../apis/ItemType.api'
import shipOrderApi, {
  ShipOrderCreateRequest, ShipOrderCreateRequestItem
} from '../apis/shipOrder.api'
import dateUtil from '../utils/date.util'
import { ConfirmParams } from '../components/Confirm/Confirm.state'
import { ActionName } from '../components/Actions'
import Card from '../components/Card'
import Row from '../components/Row/Row'
import PageHeading from '../components/PageHeading'
import InputText from '../components/InputText'
import Button from '../components/Button'
import SearchPicker, { SearchPickerOption } from '../components/SearchPicker'
import DatePicker from '../components/DatePicker/DatePicker'
import CardButton from '../components/CardButton'
import Select from '../components/Select'
import TableHorizontal, {
  TableHorizontalData,
  TableHorizontalHeader
} from '../components/TableHorizontal'

interface Props extends RouteComponentProps {
  onItemChange: (item: ItemData | null) => void
  onQuantityChange: (quantity: number) => void
}

interface State {
  item: string
  quantity: string
  customer: string
  customerId: number | null
  customerSite: string
  purchaseOrder: string
  salesOrder: string
  freightNumber: string
  shipAt: string
  deliveryAt: string
  note: string
  selectedItem: ItemData | null
  items: ItemData[]
  customers: CustomerData[]
  customerSites: ContactSiteData[]
  shipOrderItems: TableHorizontalData[]
}

class ShipOrderCreateForm extends Component<Props, State> {
    tableHeaders: TableHorizontalHeader[] = [
      { heading: i18n("Item code"), path: 'code' },
      { heading: i18n("Description"), path: 'description' },
      { heading: i18n("Quantity"), path: 'quantity' },
      { heading: i18n("Actions") }
    ]

    constructor (props: Props) {
      super(props)
      const date = dateUtil.now()
      this.state = {
        item: '',
        quantity: '',
        customer: '',
        customerId: null,
        customerSite: '',
        purchaseOrder: '',
        salesOrder: '',
        freightNumber: '',
        shipAt: date,
        deliveryAt: date,
        note: '',
        selectedItem: null,
        items: [],
        customers: [],
        customerSites: [],
        shipOrderItems: []
      }
      this.handleInputChange = this.handleInputChange.bind(this)
      this.handleAddClick = this.handleAddClick.bind(this)
      this.handleSearch = this.handleSearch.bind(this)
      this.handleActionClick = this.handleActionClick.bind(this)
      this.handleDatePickerChange = this.handleDatePickerChange.bind(this)
      this.handleSearchSelect = this.handleSearchSelect.bind(this)
      this.handleItemBlur = this.handleItemBlur.bind(this)
      this.handleCreateClick = this.handleCreateClick.bind(this)
      this.handleCustomerBlur = this.handleCustomerBlur.bind(this)
    }

    get itemSearchOptions () {
      return itemService.searchOptions(this.state.items)
    }

    get customerSearchOptions () {
      return customerService.searchOptions(this.state.customers)
    }

    get customerSiteSelectOptions () {
      return contactSiteService.selectOptions(this.state.customerSites)
    }

    get shipOrderItems ():ShipOrderCreateRequestItem[] {
      return map(this.state.shipOrderItems, shipOrderItem => {
        const { id, quantity } = shipOrderItem
        return { id, quantity }
      })
    }

    get selectedItemId () {
      const { selectedItem } = this.state
      if (!selectedItem) return null
      return selectedItem.id
    }

    handleInputChange (name: string, value: string) {
      if (name === 'item') {
        this.setState({ selectedItem: null, item: value })
        this.props.onItemChange(null)
      } else if (name === 'quantity') {
        this.setState({ quantity: value })
        this.props.onQuantityChange(Number(value) || 0)
      } else if (name === 'customer') {
        this.setState({ customerId: null, customer: value })
      } else if (name === 'customerSite') this.setState({ customerSite: value })
      else if (name === 'purchaseOrder') this.setState({ purchaseOrder: value })
      else if (name === 'salesOrder') this.setState({ salesOrder: value })
      else if (name === 'freightNumber') this.setState({ freightNumber: value })
      else if (name === 'note') this.setState({ note: value })
    }

    handleAddClick () {
      const { selectedItem, quantity } = this.state
      if (!selectedItem || !quantity) return
      const actions: ActionName[] = ['delete']
      const shipOrderItem: TableHorizontalData = selectedItem
      shipOrderItem.actions = actions
      shipOrderItem.quantity = Number(quantity)
      this.setState(state => {
        return produce(state, draft => {
          draft.shipOrderItems = reject(draft.shipOrderItems, { id: selectedItem.id })
          draft.shipOrderItems.unshift(shipOrderItem)
          draft.item = ''
          draft.quantity = ''
          draft.selectedItem = null
        })
      })
      this.props.onItemChange(null)
      this.props.onQuantityChange(0)
    }

    listCustomerSites () {
      const customerId = this.state.customerId
      if (!customerId) return
      customerApi.listSites(customerId, customerSites => {
        if (!customerSites) return
        this.setState({ customerSites })
      })
    }

    create () {
      const { shipOrderItems, customerSite, note } = this.state
      const { purchaseOrder, salesOrder, freightNumber } = this.state
      const { shipAt, deliveryAt } = this.state
      const payload: ShipOrderCreateRequest = {}
      if (shipOrderItems) payload.items = this.shipOrderItems
      if (customerSite) payload.customerSite = Number(customerSite)
      if (purchaseOrder) payload.purchaseOrder = purchaseOrder
      if (salesOrder) payload.salesOrder = salesOrder
      if (freightNumber) payload.freightNumber = freightNumber
      if (shipAt) payload.shipDatetime = shipAt
      if (deliveryAt) payload.deliveryDate = deliveryAt
      if (note) payload.note = note
      shipOrderApi.create(payload, shipOrder => {
        if (!shipOrder) return
        this.props.history.push(routes.shipOrders)
      })
    }

    handleSearch (name: string, term: string) {
      if (name === 'customer') {
        customerApi.search(term, customers => {
          if (!customers) return
          this.setState({ customers })
        })
      } else if (name === 'item') {
        const types: ItemType[] = ['production', 'receivable']
        itemApi.searchByCode(term, types, items => {
          if (!items) return
          this.setState({ items })
        })
      }
    }

    handleActionClick (id: number, action: ActionName) {
      if (action === 'delete') {
        const confirm: ConfirmParams = {
          id,
          type: 'warning',
          question: i18n('Are you sure to delete the ship order item?'),
          confirmText: i18n('Yes'),
          cancelText: i18n('No'),
          onConfirm: () => this.handleDeleteConfirm(id)
        }
        confirmService.push(confirm)
      }
    }

    handleDeleteConfirm (id: number) {
      this.setState(state => {
        return produce(state, draft => {
          const index = findIndex(draft.shipOrderItems, { id })
          pullAt(draft.shipOrderItems, index)
        })
      })
    }

    handleDatePickerChange (name: string, date: string) {
      if (name === 'shipAt') this.setState({ shipAt: date })
      if (name === 'deliveryAt') this.setState({ deliveryAt: date })
    }

    handleSearchSelect (name: string, option: SearchPickerOption) {
      if (name === 'item') {
        const { value } = option
        const itemId = option.id as number
        const selectedItem = itemService.find(this.state.items, itemId)
        if (!selectedItem) return
        this.props.onItemChange(selectedItem)
        this.setState({ items: [], item: value, selectedItem })
      } else if (name === 'customer') {
        const customerId = option.id as number
        this.setState(
          { customers: [], customerId },
          () => this.listCustomerSites()
        )
      }
    }

    handleItemBlur (name: string) {
      this.setState({ items: [] })
    }

    handleCustomerBlur (name: string) {
      this.setState({ customers: [] })
    }

    handleCreateClick () {
      const confirm: ConfirmParams = {
        id: 1,
        question: i18n('Please review ship order details above and then confirm'),
        confirmText: i18n('Confirm'),
        cancelText: i18n('Cancel'),
        onConfirm: () => this.create()
      }
      confirmService.push(confirm)
    }

    render () {
      return (
      <Card>
        <PageHeading
          text={i18n("Create Ship Order")}
          doc='ship-orders-create-ship-order' />
        <Row column={3}>
          <SearchPicker
            name="item"
            value={this.state.item}
            options={this.itemSearchOptions}
            label={i18n("Item")}
            onSearch={this.handleSearch}
            onChange={this.handleInputChange}
            onSelect={this.handleSearchSelect}
            onBlur={this.handleItemBlur} />
          <InputText
            name="quantity"
            value={this.state.quantity}
            label={i18n("Quantity")}
            onChange={this.handleInputChange} />
          <div className="flex items-end mb-4">
            <Button
              text={i18n("Add")}
              full={true}
              onClick={this.handleAddClick} />
          </div>
        </Row>
        <Row column={1}>
          <TableHorizontal
            headers={this.tableHeaders}
            data={this.state.shipOrderItems}
            onActionClick={this.handleActionClick} />
        </Row>
        <Row column={2}>
          <SearchPicker
            name="customer"
            value={this.state.customer}
            options={this.customerSearchOptions}
            label={i18n("Customer")}
            onSearch={this.handleSearch}
            onChange={this.handleInputChange}
            onSelect={this.handleSearchSelect}
            onBlur={this.handleCustomerBlur} />
          <Select
            name="customerSite"
            value={this.state.customerSite}
            options={this.customerSiteSelectOptions}
            label={i18n("Site")}
            onChange={this.handleInputChange} />
          <InputText
            name="purchaseOrder"
            value={this.state.purchaseOrder}
            label={i18n("Purchase order")}
            onChange={this.handleInputChange} />
          <InputText
            name="salesOrder"
            value={this.state.salesOrder}
            label={i18n("Sales order")}
            onChange={this.handleInputChange} />
          <InputText
            name="freightNumber"
            value={this.state.freightNumber}
            label={i18n("Freight number")}
            onChange={this.handleInputChange} />
          <DatePicker
            name="shipAt"
            value={this.state.shipAt}
            label={i18n("Ship datetime")}
            time={true}
            onChange={this.handleDatePickerChange} />
        </Row>
        <Row column={2}>
          <DatePicker
            name="deliveryAt"
            value={this.state.deliveryAt}
            label={i18n("Delivery date")}
            onChange={this.handleDatePickerChange} />
          <InputText
            name="note"
            value={this.state.note}
            label={i18n("Note")}
            onChange={this.handleInputChange} />
        </Row>
        <CardButton
          proceedText={i18n("Create")}
          cancelText={i18n("Cancel")}
          onProceedClick={this.handleCreateClick} />
      </Card>
      )
    }
}

export default withRouter(ShipOrderCreateForm)
